import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import FieldMappingEditor from './FieldMappingEditor';

const ApiKeyForm = ({ open, onClose, onSave, apiKey, generatedKey }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allowedOrigins: [],
    fieldMappings: [
      { externalField: 'name', displayName: 'Name', fieldType: 'text', required: true },
      { externalField: 'email', displayName: 'Email', fieldType: 'email', required: true },
      { externalField: 'message', displayName: 'Message', fieldType: 'textarea', required: false },
    ],
  });
  const [originInput, setOriginInput] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (apiKey) {
      setFormData({
        name: apiKey.name || '',
        description: apiKey.description || '',
        allowedOrigins: apiKey.allowedOrigins || [],
        fieldMappings: apiKey.fieldMappings || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        allowedOrigins: [],
        fieldMappings: [
          { externalField: 'name', displayName: 'Name', fieldType: 'text', required: true },
          { externalField: 'email', displayName: 'Email', fieldType: 'email', required: true },
          { externalField: 'message', displayName: 'Message', fieldType: 'textarea', required: false },
        ],
      });
    }
    setErrors({});
  }, [apiKey, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleAddOrigin = () => {
    const origin = originInput.trim();
    if (!origin) return;

    // Validate URL format
    try {
      if (!origin.startsWith('*.')) {
        new URL(origin);
      }
    } catch {
      setErrors({ ...errors, origin: 'Invalid URL format' });
      return;
    }

    if (formData.allowedOrigins.includes(origin)) {
      setErrors({ ...errors, origin: 'Origin already added' });
      return;
    }

    setFormData({
      ...formData,
      allowedOrigins: [...formData.allowedOrigins, origin],
    });
    setOriginInput('');
    setErrors({ ...errors, origin: null });
  };

  const handleRemoveOrigin = (originToRemove) => {
    setFormData({
      ...formData,
      allowedOrigins: formData.allowedOrigins.filter(o => o !== originToRemove),
    });
  };

  const handleFieldMappingsChange = (fieldMappings) => {
    setFormData({ ...formData, fieldMappings });
    if (errors.fieldMappings) {
      setErrors({ ...errors, fieldMappings: null });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.fieldMappings || formData.fieldMappings.length === 0) {
      newErrors.fieldMappings = 'At least one field mapping is required';
    } else {
      // Validate each field mapping
      const fieldErrors = [];
      const seenFields = new Set();

      formData.fieldMappings.forEach((mapping, index) => {
        const mappingErrors = {};

        if (!mapping.externalField?.trim()) {
          mappingErrors.externalField = true;
        } else if (seenFields.has(mapping.externalField)) {
          mappingErrors.externalField = true;
          newErrors.fieldMappings = 'Duplicate external field names are not allowed';
        } else {
          seenFields.add(mapping.externalField);
        }

        if (!mapping.displayName?.trim()) {
          mappingErrors.displayName = true;
        }

        if (Object.keys(mappingErrors).length > 0) {
          fieldErrors[index] = mappingErrors;
        }
      });

      if (fieldErrors.length > 0) {
        newErrors.fieldMappings = newErrors.fieldMappings || fieldErrors;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isEditing = !!apiKey;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {generatedKey ? 'API Key Created' : isEditing ? 'Edit API Key' : 'Create API Key'}
      </DialogTitle>
      <DialogContent>
        {generatedKey ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your API key has been created. Copy it now - you won't be able to see it again!
            </Alert>

            <TextField
              fullWidth
              label="API Key"
              value={generatedKey}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyKey} edge="end">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { fontFamily: 'monospace' },
              }}
              sx={{ mb: 2 }}
            />

            {copied && (
              <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                Copied to clipboard!
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this key in the <code>X-API-Key</code> header when making requests to the form submission endpoint.
            </Typography>

            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
              <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
{`fetch('${window.location.origin}/api/public/forms/submit', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': '${generatedKey}'
  },
  body: JSON.stringify(formData)
});`}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Basic Information
            </Typography>

            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name || 'A descriptive name for this API key (e.g., "Marketing Site Contact Form")'}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ my: 3 }} />

            {/* Allowed Origins */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Allowed Origins (CORS)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Specify which domains can submit forms using this API key. Leave empty to allow all origins (not recommended for production).
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Origin URL"
                placeholder="https://example.com"
                value={originInput}
                onChange={(e) => setOriginInput(e.target.value)}
                error={!!errors.origin}
                helperText={errors.origin}
                size="small"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddOrigin();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddOrigin}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {formData.allowedOrigins.length === 0 ? (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  No origins configured - all origins will be allowed
                </Typography>
              ) : (
                formData.allowedOrigins.map((origin) => (
                  <Chip
                    key={origin}
                    label={origin}
                    onDelete={() => handleRemoveOrigin(origin)}
                    deleteIcon={<CloseIcon />}
                  />
                ))
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Field Mappings */}
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Field Configuration
            </Typography>

            <FieldMappingEditor
              fieldMappings={formData.fieldMappings}
              onChange={handleFieldMappingsChange}
              errors={errors}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {generatedKey ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create API Key'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyForm;
